import React, { ReactNode } from 'react';
import { UserProvider } from './user/UserProvider';
import { MenuProvider } from './app/menuContext';
import { DomainProvider } from './DomainContext';
import CampaignConfigProvider from './config/CampaignConfigProvider';
import AccountConfigProvider from './config/AccountConfigProvider';
import { MyIntegrationsProvider } from './integration/IntegrationContext';
import TemplateProvider from './templates/TemplatesProvider';
import CatalogsProvider from './catalogs/CatalogsProvider';
import IndustriesProvider from './industries/IndustriesProvider';
import AssistantChatProvider from './app/AssistantChatProvider';
import { CopilotChatProvider } from '../fusion-copilot/context/CopilotChatProvider';
import { OnboardingProvider } from './onboarding';
import posthog from 'posthog-js';

interface ProvidersProps {
	children: ReactNode;
}

const assistantAlias = 'distillery-assistant';

class ErrorBoundary extends React.Component<
	{ children: React.ReactNode },
	{ hasError: boolean; error?: Error }
> {
	constructor(props: { children: React.ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);

		// Capture error to PostHog with detailed context
		try {
			posthog.captureException(error, {
				$exception_message: error.message,
				$exception_type: error.name,
				$exception_stack_trace_raw: error.stack,
				errorInfo: errorInfo.componentStack,
				url: window.location.href,
				userAgent: navigator.userAgent,
			});

			// Also log as a custom event for easier filtering
			posthog.capture('error_boundary_triggered', {
				error_message: error.message,
				error_name: error.name,
				error_stack: error.stack,
				component_stack: errorInfo.componentStack,
				page_url: window.location.href,
			});
		} catch (posthogError) {
			console.error('Failed to capture error to PostHog:', posthogError);
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<div style={{ padding: '20px', textAlign: 'center' }}>
					<h2>Something went wrong.</h2>
					<button onClick={() => this.setState({ hasError: false })}>
						Try again
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}

const AuthenticationProviders: React.FC<{ children: ReactNode }> = ({
	children,
}) => (
	<UserProvider>
		<MenuProvider>
			<DomainProvider>{children}</DomainProvider>
		</MenuProvider>
	</UserProvider>
);

const ConfigurationProviders: React.FC<{ children: ReactNode }> = ({
	children,
}) => (
	<CampaignConfigProvider>
		<AccountConfigProvider>
			<MyIntegrationsProvider>{children}</MyIntegrationsProvider>
		</AccountConfigProvider>
	</CampaignConfigProvider>
);

const DataProviders: React.FC<{ children: ReactNode }> = ({ children }) => (
	<TemplateProvider>
		<CatalogsProvider>
			<IndustriesProvider>{children}</IndustriesProvider>
		</CatalogsProvider>
	</TemplateProvider>
);

const CommunicationProviders: React.FC<{ children: ReactNode }> = ({
	children,
}) => (
	<CopilotChatProvider assistantAlias={assistantAlias}>
		<AssistantChatProvider>
			<OnboardingProvider>{children}</OnboardingProvider>
		</AssistantChatProvider>
	</CopilotChatProvider>
);

export const Providers: React.FC<ProvidersProps> = ({ children }) => {
	return (
		<ErrorBoundary>
			<AuthenticationProviders>
				<ErrorBoundary>
					<ConfigurationProviders>
						<ErrorBoundary>
							<DataProviders>
								<ErrorBoundary>
									<CommunicationProviders>{children}</CommunicationProviders>
								</ErrorBoundary>
							</DataProviders>
						</ErrorBoundary>
					</ConfigurationProviders>
				</ErrorBoundary>
			</AuthenticationProviders>
		</ErrorBoundary>
	);
};

export default Providers;
